import { CircularProgress, Container, Box, Typography, Backdrop, Snackbar, Alert } from '@mui/material';
import SentimentVeryDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentVeryDissatisfiedOutlined';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useNavigate } from 'react-router-dom';
import {
  loadEvent,
  refreshEvent,
  selectEvent,
  selectStatus,
  selectErrorMessage,
  selectCompleteErrorMessage,
  selectCompletionMessage,
  selectIsImmediateCompletionAttempt,
  selectValidationErrors,
  completeEvent as completeEventAction,
  cancelCompleteEvent as cancelCompleteEventAction,
  resetCompleteStatus,
  loadFailedImageDetails,
  checkFailedCameraUploads,
  selectEventCompleted,
  deleteCamera,
  selectDeleteCameraErrorMessage,
} from '../../app/slices/eventsSlice';
import PageHeader from '../commons/PageHeader';
import EventDetailsExpansionPanel from './EventDetailsExpansionPanel';
import LinkedCameras from './LinkedCameras';
import { selectAssumedRole, selectCanDeleteCamera, selectUser } from '../../app/slices/userSlice';
import EventStatusCardContainer from './eventStatus/EventStatusCardContainer';
import EventValidationModal from '../eventValidation/EventValidationModal';
import { dateFormatter } from '../../app/helper/dateFormatHelper';
import { EventCompleteRequest, OverrideRequest } from '../../model/event-complete-request';
import { EventCompletionMode } from '../../model/eventCompletionMode';
import EventCompletionModal from './eventCompletionModal/EventCompletionModal';
import { Camera } from '../../model/camera';
import CameraDeleteRequest from '../../model/camera-delete-request';
import { ADMIN_ACCESS } from '../commons/AccesRoles';

function EventDetails() {
  const dispatch = useAppDispatch();

  const eventDetails = useAppSelector(selectEvent);
  const failedCamera = useAppSelector(checkFailedCameraUploads);
  const userRole = useAppSelector(selectAssumedRole);
  const userCanDeleteCamera = useAppSelector(selectCanDeleteCamera);
  const eventCompleted = useAppSelector(selectEventCompleted);
  const { name } = useAppSelector(selectUser);

  const { eventId } = useParams();
  const { status, completeStatus, deleteCameraStatus } = useAppSelector(selectStatus);
  const errorMessage = useAppSelector(selectErrorMessage);
  const completeErrorMessage = useAppSelector(selectCompleteErrorMessage);
  const completionMessage = useAppSelector(selectCompletionMessage);
  const isImmediateCompletionAttempt = useAppSelector(selectIsImmediateCompletionAttempt);
  const deleteCameraErrorMessage = useAppSelector(selectDeleteCameraErrorMessage);
  const validationErrors = useAppSelector(selectValidationErrors);

  const prettyEventDates = dateFormatter(eventDetails);
  const navigate = useNavigate();
  const [alert, setAlert] = useState(false);
  const [alertErrorMessage, setAlertErrorMessage] = useState('');

  const handleTimerEnd = (eventId: string) => {
    if (eventId) {
      dispatch(loadEvent(eventId));
    }
  };

  useEffect(() => {
    if (!eventDetails && !!eventId) {
      dispatch(loadEvent(eventId));
    }
  }, [dispatch, eventId, eventDetails]);

  useEffect(() => {
    if (completeStatus === 'FAILED' && completeErrorMessage) {
      setAlert(true);
      setAlertErrorMessage(completeErrorMessage);
    }
  }, [completeStatus, completeErrorMessage]);

  useEffect(() => {
    if (deleteCameraStatus === 'FAILED' && deleteCameraErrorMessage) {
      setAlert(true);
      setAlertErrorMessage(deleteCameraErrorMessage);
    }
  }, [deleteCameraStatus, deleteCameraErrorMessage]);

  useEffect(() => {
    // Need to call only if camera upload failed
    if (!!eventDetails && !eventDetails.failedCameraUploads && !!eventId && failedCamera) {
      dispatch(loadFailedImageDetails(eventId));
    }
  }, [dispatch, eventId, failedCamera, eventDetails]);

  // auto refresh
  useEffect(() => {
    if (eventDetails?.eventId && eventDetails?.status !== 'COMPLETE') {
      const refresh = () => dispatch(refreshEvent(eventDetails.eventId!));
      const intervalCode = window.setInterval(refresh, 15 * 1000);

      return () => window.clearInterval(intervalCode);
    }
  }, [dispatch, eventDetails?.eventId, eventDetails?.status]);

  const canCompleteEvent =
    (userRole === 'AppLead' || ADMIN_ACCESS.includes(userRole || '')) &&
    (eventDetails?.status === 'PENDING' || eventDetails?.status === 'PENDING_EVENT_COMPLETION');

  const completeEvent = (overrideRequest: OverrideRequest, eventCompletionMode: EventCompletionMode) => {
    if (eventId) {
      const eventCompleteRequest: EventCompleteRequest = {
        eventId,
        overrideRequest,
        completionMode: eventCompletionMode,
      };
      dispatch(completeEventAction(eventCompleteRequest));
    }
  };

  const cancelEventComplete = () => {
    if (eventId) {
      dispatch(cancelCompleteEventAction(eventId));
    }
  };

  const handleCompletionModalOnClose = (backToHome: boolean) => {
    dispatch(resetCompleteStatus());

    if (backToHome) {
      navigate('/');
    }
  };

  const closeAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlert(false);
  };

  const deleteCameraHandler = (camera: Camera) => {
    dispatch(
      deleteCamera({
        eventId: eventDetails?.eventId,
        deleteRequest: {
          cameraId: camera.cameraId,
          mediaIdentifier: camera.mediaId,
        } as CameraDeleteRequest,
      })
    );
  };

  return (
    <>
      <PageHeader title="Event Details" />
      <Container>
        {status === 'LOADING' || !eventDetails ? (
          <Box sx={{ textAlign: 'center', mt: 22 }}>
            {status === 'FAILED' ? (
              <div>
                <SentimentVeryDissatisfiedOutlinedIcon sx={{ fontSize: 56 }} color="error" />
                <Typography variant="h2" color="error">
                  Failed To Load Event {eventId}
                </Typography>
                <Typography variant="h4" color="error">
                  {errorMessage}
                </Typography>
              </div>
            ) : (
              <CircularProgress />
            )}
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: 'row',
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 1,
              }}
            >
              <Box sx={{ flex: 6, flexShrink: 0 }}>
                <EventDetailsExpansionPanel eventDetails={eventDetails} eventDates={prettyEventDates} />
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                  <EventStatusCardContainer
                    eventDetails={eventDetails}
                    canCompleteEvent={canCompleteEvent}
                    completeEvent={completeEvent}
                    cancelEventComplete={cancelEventComplete}
                    onTimerEnd={handleTimerEnd}
                  />
                </Box>
                <LinkedCameras
                  eventDetails={eventDetails}
                  userCanDeleteCamera={userCanDeleteCamera && !eventCompleted}
                  onDeleteCamera={(camera) => deleteCameraHandler(camera)}
                />
              </Box>
              <Box
                sx={{
                  mt: 0,
                  ml: 2,
                  flex: 2,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                <EventStatusCardContainer
                  eventDetails={eventDetails}
                  canCompleteEvent={canCompleteEvent}
                  completeEvent={completeEvent}
                  cancelEventComplete={cancelEventComplete}
                  onTimerEnd={handleTimerEnd}
                />
              </Box>
            </Box>
          </>
        )}
      </Container>

      <Snackbar
        open={alert}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={closeAlert}
      >
        <Alert severity="error">{alertErrorMessage}</Alert>
      </Snackbar>

      <EventValidationModal
        validationErrors={validationErrors}
        eventDates={prettyEventDates}
        eventId={eventId}
        userName={name}
        completeStatus={completeStatus}
        canCompleteEvent={canCompleteEvent}
        completeEvent={completeEvent}
        userRole={userRole}
        isImmediateCompletionAttempt={isImmediateCompletionAttempt}
      />

      <EventCompletionModal
        validationErrors={validationErrors || []}
        open={completeStatus === 'SUCCESS'}
        onClose={handleCompletionModalOnClose}
        eventDates={prettyEventDates}
        eventId={eventId || ''}
        userName={name || ''}
        pictureDays={eventDetails?.days || []}
        overrideReasons={eventDetails?.overrideReasons}
        completionMessage={completionMessage}
      />
      <Backdrop open={completeStatus === 'LOADING' || deleteCameraStatus === 'LOADING'} sx={{ zIndex: 1202 }}>
        <CircularProgress />
      </Backdrop>
    </>
  );
}

export default EventDetails;
